// Privacy-first Mixpanel client wrapper. See docs/epics/mixpanel-analytics.md.

import mixpanel from 'mixpanel-browser';
import type { EventName, PropsFor } from './analytics-events';
import {
  requiresConsent,
  getStoredConsent,
  storeConsent,
  type ConsentDecision,
} from './region';
import { maybeInitSingular, stopSingular, resumeSingular, forwardToSingular } from './singular';

// Mixpanel project tokens are publishable identifiers, safe in client code.
const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN || '863b60819298747768752549f64d0daa';

let initialized = false;
let warned = false;

function isTrackingActive(): boolean {
  if (typeof window === 'undefined') return false;
  if (!TOKEN) {
    if (!warned && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] VITE_MIXPANEL_TOKEN is not set; analytics will no-op.');
      warned = true;
    }
    return false;
  }
  if (!initialized) return false;
  try {
    if (mixpanel.has_opted_out_tracking?.()) return false;
  } catch {
    /* not yet hydrated */
  }
  return true;
}

export function initIfReady(): void {
  if (typeof window === 'undefined') return;

  // Native Singular self-gates on consent + platform; safe to poke every call.
  void maybeInitSingular();

  if (initialized) return;
  if (!TOKEN) return;
  if (requiresConsent() && getStoredConsent() !== 'granted') return;

  mixpanel.init(TOKEN, {
    persistence: 'localStorage',
    track_pageview: false,
    ip: false,
    autocapture: false,
    debug: import.meta.env.DEV,
    property_blacklist: ['$current_url', '$initial_referrer', '$referrer'],
  });

  initialized = true;
}

export function track<E extends EventName>(
  eventName: E,
  ...args: keyof PropsFor<E> extends never ? [] : [PropsFor<E>]
): void {
  if (!isTrackingActive()) return;
  forwardToSingular(eventName);
  try {
    mixpanel.track(eventName, args[0] as Record<string, unknown> | undefined);
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] track() failed:', err);
    }
  }
}

export function identify(userId: string): void {
  if (!isTrackingActive()) return;
  try {
    mixpanel.identify(userId);
  } catch {
    /* no-op */
  }
}

export function reset(): void {
  if (!isTrackingActive()) return;
  try {
    mixpanel.reset();
  } catch {
    /* no-op */
  }
}

export function setUserProperties(props: Record<string, unknown>): void {
  if (!isTrackingActive()) return;
  try {
    mixpanel.people.set(props);
  } catch {
    /* no-op */
  }
}

export function optOut(): void {
  stopSingular();
  if (!initialized || typeof window === 'undefined') return;
  try {
    mixpanel.opt_out_tracking();
  } catch {
    /* no-op */
  }
}

export function optIn(): void {
  resumeSingular();
  if (!initialized || typeof window === 'undefined') return;
  try {
    mixpanel.opt_in_tracking();
  } catch {
    /* no-op */
  }
}

export function isOptedOut(): boolean {
  if (!initialized || typeof window === 'undefined') return false;
  try {
    return Boolean(mixpanel.has_opted_out_tracking?.());
  } catch {
    return false;
  }
}

export function grantConsent(): void {
  storeConsent('granted');
  initIfReady();
}

export function denyConsent(): void {
  storeConsent('denied');
  if (initialized) optOut();
  stopSingular();
}

export function hasConsent(): ConsentDecision | null {
  return getStoredConsent();
}

export { requiresConsent } from './region';
